import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Plus,
  Trash2,
  Pencil,
  Loader2,
  ArrowLeft,
  ArrowRight,
  Check,
  Package,
  Truck,
  FileText,
  Coins,
  Ship,
  Plane,
  Train,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useToast } from '@/lib/hooks/use-toast';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { importTrackingService } from '../services/import-tracking-service';
import { customerService } from '@/features/customers/services/customer-service';
import type { Customer } from '@/features/customers/types/customer.types';
import {
  freightLocationsService,
  FREIGHT_LOCATION_TYPE_LABELS,
  type FreightLocation,
} from '@/features/master-data/services/freight-locations-service';
import type {
  Incoterm,
  CreateImportOrderInput,
  ImportOrderItem,
  ImportOrderLeg,
} from '../types/import-tracking.types';
import { ProductFormDialog } from './product-form-dialog';
import { LegFormDialog } from './leg-form-dialog';
import { LegCatalogPicker } from './leg-catalog-picker';
import { LegPricingDialog } from './leg-pricing-dialog';
import type { CatalogFreightLeg } from '@/features/master-data/services/freight-leg-catalog-service';

// ── Constantes ──────────────────────────────────────────────────────
const TRANSPORT_ICONS: Record<string, React.ReactNode> = {
  SEA: <Ship className="w-3.5 h-3.5" />,
  AIR: <Plane className="w-3.5 h-3.5" />,
  LAND: <Truck className="w-3.5 h-3.5" />,
  RAIL: <Train className="w-3.5 h-3.5" />,
  MULTIMODAL: <Truck className="w-3.5 h-3.5" />,
};

const INCOTERM_OPTIONS = [
  { value: 'EXW', label: 'EXW - Ex Works' },
  { value: 'FCA', label: 'FCA - Free Carrier' },
  { value: 'FAS', label: 'FAS - Free Alongside Ship' },
  { value: 'FOB', label: 'FOB - Free On Board' },
  { value: 'CFR', label: 'CFR - Cost and Freight' },
  { value: 'CIF', label: 'CIF - Cost, Insurance & Freight' },
  { value: 'CPT', label: 'CPT - Carriage Paid To' },
  { value: 'CIP', label: 'CIP - Carriage & Insurance Paid' },
  { value: 'DAP', label: 'DAP - Delivered At Place' },
  { value: 'DPU', label: 'DPU - Delivered At Place Unloaded' },
  { value: 'DDP', label: 'DDP - Delivered Duty Paid' },
];

// ── Props ───────────────────────────────────────────────────────────
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (orderId: string) => void;
  /*
    No hay `defaultIsQuote`: este asistente crea SIEMPRE una cotización
    (`isQuote: true`, más abajo). La prop existía, se pasaba desde la página y
    nadie la leía — el tipo prometía una opción que no existe.

    Lo que sí queda anotado: "Nueva orden" en Import/Export abre este mismo
    asistente, así que ese botón también crea una cotización. El nombre no dice
    la verdad, pero cambiarlo es una decisión de producto.
  */
}

type StepId = 1 | 2 | 3;

// ── Componente principal ────────────────────────────────────────────
export function ImportOrderWizard({ open, onOpenChange, onSuccess }: Props) {
  const { createOrder } = useImportTrackingStore();
  const { toast } = useToast();

  const [step, setStep] = useState<StepId>(1);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [orderCode, setOrderCode] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Step 1 state — datos de la importación ────────────────────────
  // Las fechas estimadas, modo de transporte y ruta de flete se calculan
  // a partir de los tramos (paso 3). Origen/Destino vienen del catálogo
  // de Locations en master-data. La importación siempre nace como
  // cotización; se convierte a orden firme desde el detalle.
  const [tradeType, setTradeType] = useState<'IMPORT' | 'EXPORT'>('IMPORT');
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [incoterm, setIncoterm] = useState<Incoterm>('FOB');
  const [originLocationId, setOriginLocationId] = useState<string>('');
  const [destinationLocationId, setDestinationLocationId] = useState<string>('');
  const [locations, setLocations] = useState<FreightLocation[]>([]);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // ── Step 2 state — productos ──────────────────────────────────────
  const [products, setProducts] = useState<ImportOrderItem[]>([]);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ImportOrderItem | null>(null);

  // ── Step 3 state — tramos ─────────────────────────────────────────
  const [legs, setLegs] = useState<ImportOrderLeg[]>([]);
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [legDialogState, setLegDialogState] = useState<
    { open: false } | { open: true; mode: { kind: 'edit'; leg: ImportOrderLeg } }
  >({ open: false });
  const [pricingLegId, setPricingLegId] = useState<string | null>(null);

  // ── Effects ───────────────────────────────────────────────────────
  // Reset al abrir
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setOrderId(null);
    setOrderCode('');
    setTradeType('IMPORT');
    setCustomerId(null);
    setOrderDate(new Date().toISOString().split('T')[0]);
    setIncoterm('FOB');
    setOriginLocationId('');
    setDestinationLocationId('');
    setPaymentTerms('');
    setNotes('');
    setShowAdvanced(false);
    setProducts([]);
    setLegs([]);
  }, [open]);

  // Cargar customers y locations
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    freightLocationsService
      .list({ isActive: true })
      .then((data) => {
        if (!cancelled) setLocations(data);
      })
      .catch(() => {});
    customerService
      .getCustomers()
      .then((data) => {
        if (!cancelled) setCustomers(data);
      })
      .catch(() => {
        if (!cancelled) setCustomers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // ── Loaders ───────────────────────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = await importTrackingService.getOrderItems(orderId);
      setProducts(data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  }, [orderId]);

  const fetchLegs = useCallback(async () => {
    if (!orderId) return;
    try {
      const data = await importTrackingService.getOrderLegs(orderId);
      setLegs(data);
    } catch (err) {
      console.error('Error fetching legs:', err);
    }
  }, [orderId]);

  // Totales agregados de los items: peso y volumen totales (qty × unitario)
  // se usan para elegir la forma de cobro de cada tramo en step 3.
  const productTotals = useMemo(() => {
    let qty = 0;
    let weightKg = 0;
    let volumeCbm = 0;
    let fob = 0;
    for (const p of products) {
      qty += p.quantity || 0;
      if (p.weightKg) weightKg += p.quantity * p.weightKg;
      if (p.volumeCbm) volumeCbm += p.quantity * p.volumeCbm;
      fob += p.totalFobPrice || 0;
    }
    return { qty, weightKg, volumeCbm, fob };
  }, [products]);

  useEffect(() => {
    if (step === 2 && orderId) fetchProducts();
    if (step === 3 && orderId) {
      fetchLegs();
      fetchProducts(); // refresca productos también para el resumen lateral
    }
  }, [step, orderId, fetchProducts, fetchLegs]);

  // ── Acciones ──────────────────────────────────────────────────────
  const handleCreateOrder = async () => {
    if (!customerId) {
      toast({ title: 'Falta cliente', variant: 'destructive' });
      return;
    }
    const originLoc = locations.find((l) => l.id === originLocationId);
    const destinationLoc = locations.find((l) => l.id === destinationLocationId);

    setIsSubmitting(true);
    try {
      // Notas: las fechas estimadas, modo de transporte y ruta se derivan
      // de los tramos del paso 3. La importación nace siempre como cotización.
      const data: CreateImportOrderInput = {
        customerId,
        orderDate,
        incoterm,
        tradeType,
        // Snapshot del nombre de la location para retrocompat con
        // displays existentes que muestran originPort/destinationPort
        originPort: originLoc?.name || undefined,
        destinationPort: destinationLoc?.name || undefined,
        paymentTerms: paymentTerms || undefined,
        notes: notes || undefined,
        isQuote: true,
      };
      const order = await createOrder(data);
      setOrderId(order.id);
      setOrderCode(order.orderNumber ?? order.id);
      setStep(2);
      toast({ title: 'Cotización creada', description: 'Ahora agregá los productos.' });
    } catch {
      // Error ya manejado en el store (toast)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProduct = async (item: ImportOrderItem) => {
    if (!orderId) return;
    if (
      !(await confirmar({
        title: `¿Eliminar item "${item.description}"?`,
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await importTrackingService.deleteOrderItem(orderId, item.id);
      toast({ title: 'Producto eliminado' });
      fetchProducts();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo eliminar',
        variant: 'destructive',
      });
    }
  };

  // Crea un tramo en la orden a partir de un tramo del catálogo. El backend
  // hace auto-fill del pricing del proveedor activo cuando aplique.
  const handlePickCatalogLeg = async (catalogLeg: CatalogFreightLeg) => {
    if (!orderId) return;
    try {
      await importTrackingService.createLeg(orderId, {
        origin: catalogLeg.origin,
        destination: catalogLeg.destination,
        originLocationId:
          (catalogLeg as { originLocationId?: string | null }).originLocationId ?? null,
        destinationLocationId:
          (catalogLeg as { destinationLocationId?: string | null }).destinationLocationId ?? null,
        transportMode: catalogLeg.transportMode,
        legType: catalogLeg.legType,
        carrier: catalogLeg.provider?.name ?? null,
        // Pasar el providerId del catálogo para que el LegPricingDialog pueda
        // cargar las tarifas configuradas de ese proveedor.
        providerId: catalogLeg.providerId ?? null,
        legCatalogId: catalogLeg.id,
        // Propagar totales de items para que el pricing por peso/volumen
        // del tramo se calcule automáticamente.
        cargoWeightKg: productTotals.weightKg > 0 ? productTotals.weightKg : null,
        cargoVolumeCbm: productTotals.volumeCbm > 0 ? productTotals.volumeCbm : null,
      } as Parameters<typeof importTrackingService.createLeg>[1]);
      setCatalogPickerOpen(false);
      toast({
        title: 'Tramo agregado del catálogo',
        description: `${catalogLeg.origin} → ${catalogLeg.destination}`,
      });
      fetchLegs();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo agregar',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteLeg = async (leg: ImportOrderLeg) => {
    if (
      !(await confirmar({
        title: `¿Eliminar tramo ${leg.legOrder} (${leg.origin} → ${leg.destination})?`,
        variant: 'destructive',
        confirmText: 'Sí, eliminar',
      }))
    )
      return;
    try {
      await importTrackingService.deleteLeg(leg.id);
      toast({ title: 'Tramo eliminado' });
      fetchLegs();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.message ?? 'No se pudo eliminar',
        variant: 'destructive',
      });
    }
  };

  const handleFinish = () => {
    if (orderId) onSuccess?.(orderId);
    onOpenChange(false);
  };

  // ── Stepper UI ────────────────────────────────────────────────────
  const stepDef = [
    { id: 1 as StepId, label: 'Datos', icon: FileText },
    { id: 2 as StepId, label: 'Items', icon: Package },
    { id: 3 as StepId, label: 'Tramos', icon: Truck },
  ];

  const Stepper = () => (
    <div className="flex items-center justify-between px-2 mb-4">
      {stepDef.map((s, i) => {
        const Icon = s.icon;
        const isActive = step === s.id;
        const isComplete = step > s.id;
        return (
          <div key={s.id} className="flex items-center flex-1 last:flex-none">
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors',
                isActive && 'bg-primary text-primary-foreground',
                isComplete && 'text-ok',
                !isActive && !isComplete && 'text-muted-foreground'
              )}
            >
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                  isActive && 'bg-primary-foreground text-primary',
                  isComplete && 'bg-ok-suave text-ok',
                  !isActive && !isComplete && 'bg-muted text-muted-foreground'
                )}
              >
                {isComplete ? <Check className="w-3.5 h-3.5" /> : s.id}
              </div>
              <Icon className="w-3.5 h-3.5" />
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {i < stepDef.length - 1 && (
              <div className={cn('flex-1 h-px mx-2', step > s.id ? 'bg-ok' : 'bg-border')} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────
  return (
    <>
      <Dialog
        open={
          open && !productDialogOpen && !legDialogState.open && !pricingLegId && !catalogPickerOpen
        }
        onOpenChange={onOpenChange}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Nueva orden
              {orderCode && (
                <span className="ml-2 text-sm font-mono text-muted-foreground">— {orderCode}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {step === 1 && 'Completá los datos básicos de la orden.'}
              {step === 2 && 'Agregá los productos que vas a importar.'}
              {step === 3 && 'Configurá los tramos del viaje y sus precios por proveedor.'}
            </DialogDescription>
          </DialogHeader>

          <Stepper />

          {/* ──────── STEP 1: DATOS ──────── */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label>
                  Tipo de operación <span className="text-destructive">*</span>
                </Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setTradeType('IMPORT')}
                    className={
                      'rounded-md border px-3 py-2 text-sm font-medium transition-colors ' +
                      (tradeType === 'IMPORT'
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background hover:bg-accent')
                    }
                  >
                    🛬 Importación
                  </button>
                  <button
                    type="button"
                    onClick={() => setTradeType('EXPORT')}
                    className={
                      'rounded-md border px-3 py-2 text-sm font-medium transition-colors ' +
                      (tradeType === 'EXPORT'
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background hover:bg-accent')
                    }
                  >
                    🛫 Exportación
                  </button>
                </div>
              </div>

              <div>
                <Label>
                  Cliente <span className="text-destructive">*</span>
                </Label>
                <SearchableSelect
                  options={customers.map((c) => ({ value: c.id, label: c.name }))}
                  value={customerId ?? ''}
                  onValueChange={(v) => setCustomerId(v || null)}
                  placeholder="Selecciona el cliente final..."
                  searchPlaceholder="Buscar cliente..."
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  El proveedor de transporte y sus precios se asignan a cada tramo en el paso 3.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>
                    Fecha Orden <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={orderDate}
                    onChange={(e) => setOrderDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Incoterm</Label>
                  <SearchableSelect
                    options={INCOTERM_OPTIONS}
                    value={incoterm}
                    onValueChange={(v) => setIncoterm(v as Incoterm)}
                    placeholder="Incoterm..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Origen</Label>
                  <SearchableSelect
                    value={originLocationId}
                    onValueChange={setOriginLocationId}
                    placeholder="Seleccionar localización..."
                    searchPlaceholder="Buscar..."
                    emptyMessage="Cargá localizaciones en datos maestros"
                    options={locations.map((l) => ({
                      value: l.id,
                      label: `${l.code} · ${l.name}${l.city ? ` (${l.city})` : ''} — ${FREIGHT_LOCATION_TYPE_LABELS[l.type]}`,
                    }))}
                  />
                </div>
                <div>
                  <Label>Destino</Label>
                  <SearchableSelect
                    value={destinationLocationId}
                    onValueChange={setDestinationLocationId}
                    placeholder="Seleccionar localización..."
                    searchPlaceholder="Buscar..."
                    emptyMessage="Cargá localizaciones en datos maestros"
                    options={locations.map((l) => ({
                      value: l.id,
                      label: `${l.code} · ${l.name}${l.city ? ` (${l.city})` : ''} — ${FREIGHT_LOCATION_TYPE_LABELS[l.type]}`,
                    }))}
                  />
                </div>
              </div>

              <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                Las fechas estimadas, modo de transporte y proveedores se derivan de los{' '}
                <strong className="text-foreground">tramos del paso 3</strong>. La orden se crea en
                estado <strong className="text-foreground">cotización</strong> y se confirma cuando
                estás listo para emitirla.
              </div>

              {/* Más opciones */}
              <button
                type="button"
                onClick={() => setShowAdvanced((s) => !s)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                {showAdvanced ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                Más opciones
              </button>

              {showAdvanced && (
                <div className="space-y-3 pl-4 border-l-2 border-border">
                  <div>
                    <Label>Términos de pago</Label>
                    <Input
                      value={paymentTerms}
                      onChange={(e) => setPaymentTerms(e.target.value)}
                      placeholder="30 días, contado..."
                    />
                  </div>
                  <div>
                    <Label>Notas</Label>
                    <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ──────── STEP 2: PRODUCTOS ──────── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Los productos definen peso, volumen y valor — base para los cálculos de pricing.
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setEditingProduct(null);
                    setProductDialogOpen(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar item
                </Button>
              </div>

              {products.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <Package className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Aún no hay productos. Podés agregar al menos uno antes de configurar los tramos.
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-3 py-2">Descripción</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Peso total (kg)</th>
                        <th className="text-right px-3 py-2">Vol total (m³)</th>
                        <th className="text-right px-3 py-2">Total FOB</th>
                        <th className="text-right px-3 py-2">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((p) => {
                        const itemWeight = p.weightKg != null ? p.quantity * p.weightKg : null;
                        const itemVolume = p.volumeCbm != null ? p.quantity * p.volumeCbm : null;
                        return (
                          <tr key={p.id} className="border-t">
                            <td className="px-3 py-2 font-medium">{p.description}</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {p.quantity} {p.unitOfMeasure ?? ''}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {itemWeight != null ? itemWeight.toFixed(2) : '—'}
                              {p.weightKg != null && (
                                <div className="text-[10px] text-muted-foreground font-normal">
                                  {p.weightKg}/u
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {itemVolume != null ? itemVolume.toFixed(4) : '—'}
                              {p.volumeCbm != null && (
                                <div className="text-[10px] text-muted-foreground font-normal">
                                  {p.volumeCbm}/u
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {p.totalFobPrice != null
                                ? `USD ${Number(p.totalFobPrice).toFixed(2)}`
                                : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    setEditingProduct(p);
                                    setProductDialogOpen(true);
                                  }}
                                  className="h-7 w-7"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteProduct(p)}
                                  className="h-7 w-7"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-ok-suave border-t-2 border-ok-borde font-semibold text-xs uppercase tracking-wide">
                      <tr>
                        <td className="px-3 py-2 text-foreground">Totales</td>
                        <td className="px-3 py-2 text-right font-mono text-foreground">
                          {productTotals.qty}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-foreground">
                          {productTotals.weightKg.toFixed(2)} kg
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-foreground">
                          {productTotals.volumeCbm.toFixed(4)} m³
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-foreground">
                          USD {productTotals.fob.toFixed(2)}
                        </td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ──────── STEP 3: TRAMOS ──────── */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Resumen de carga (totales de items) — base para elegir
                  cómo cobrar cada tramo (Kg / m³ / fijo) */}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md border border-ok-borde bg-ok-suave px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-ok font-semibold">
                    Peso total
                  </div>
                  <div className="text-base font-bold font-mono text-foreground">
                    {productTotals.weightKg.toFixed(2)}{' '}
                    <span className="text-xs font-normal">kg</span>
                  </div>
                </div>
                <div className="rounded-md border border-ok-borde bg-ok-suave px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-ok font-semibold">
                    Volumen total
                  </div>
                  <div className="text-base font-bold font-mono text-foreground">
                    {productTotals.volumeCbm.toFixed(4)}{' '}
                    <span className="text-xs font-normal">m³</span>
                  </div>
                </div>
                <div className="rounded-md border border-ok-borde bg-ok-suave px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-ok font-semibold">
                    FOB total
                  </div>
                  <div className="text-base font-bold font-mono text-foreground">
                    USD {productTotals.fob.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Seleccioná tramos pre-configurados desde el catálogo. Click en{' '}
                  <strong>Precio</strong> para elegir cómo cobra cada proveedor (Kg / m³ / fijo) y
                  agregar servicios extra.
                </div>
                <Button type="button" size="sm" onClick={() => setCatalogPickerOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar del catálogo
                </Button>
              </div>

              {legs.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed rounded-lg">
                  <Truck className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Sin tramos. Seleccioná uno del catálogo de master-data.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {legs.map((leg) => {
                    const Icon = TRANSPORT_ICONS[leg.transportMode] ?? (
                      <Truck className="w-3.5 h-3.5" />
                    );
                    return (
                      <div key={leg.id} className="border rounded-lg p-3 flex items-center gap-3">
                        <div className="text-xs font-bold w-6 h-6 bg-muted rounded-full flex items-center justify-center">
                          {leg.legOrder}
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          {Icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">
                            {leg.origin} → {leg.destination}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {leg.carrier ?? 'Sin transportista'}
                            {leg.calculatedAmount != null && (
                              <>
                                {' '}
                                ·{' '}
                                <span className="font-mono text-ok">
                                  {leg.currency} {Number(leg.calculatedAmount).toFixed(2)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPricingLegId(leg.id)}
                          className="h-7"
                        >
                          <Coins className="w-3.5 h-3.5 mr-1" />
                          Precio
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            setLegDialogState({ open: true, mode: { kind: 'edit', leg } })
                          }
                          className="h-7 w-7"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteLeg(leg)}
                          className="h-7 w-7"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ──────── FOOTER ──────── */}
          <DialogFooter className="border-t pt-4 mt-4 flex items-center sm:justify-between">
            <div className="flex gap-2">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep((step - 1) as StepId)}
                  disabled={isSubmitting}
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Atrás
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {orderId ? 'Cerrar' : 'Cancelar'}
              </Button>
              {step === 1 && (
                <Button type="button" onClick={handleCreateOrder} disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Crear y continuar
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 2 && (
                <Button type="button" onClick={() => setStep(3)}>
                  Siguiente
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              {step === 3 && (
                <Button type="button" onClick={handleFinish}>
                  <Check className="w-4 h-4 mr-1" />
                  Finalizar
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sub-dialogs */}
      {orderId && (
        <ProductFormDialog
          orderId={orderId}
          open={productDialogOpen}
          onOpenChange={setProductDialogOpen}
          item={editingProduct ?? undefined}
          onSuccess={() => {
            setProductDialogOpen(false);
            fetchProducts();
          }}
        />
      )}

      {orderId && (
        <LegCatalogPicker
          open={catalogPickerOpen}
          onOpenChange={setCatalogPickerOpen}
          onPick={handlePickCatalogLeg}
        />
      )}

      {orderId && legDialogState.open && (
        <LegFormDialog
          open={legDialogState.open}
          onOpenChange={(o) => {
            if (!o) setLegDialogState({ open: false });
          }}
          mode={{ kind: 'edit', leg: legDialogState.mode.leg }}
          onSuccess={() => {
            setLegDialogState({ open: false });
            fetchLegs();
          }}
        />
      )}

      {pricingLegId &&
        (() => {
          const leg = legs.find((l) => l.id === pricingLegId);
          if (!leg) return null;
          return (
            <LegPricingDialog
              open={!!pricingLegId}
              onOpenChange={(o) => {
                if (!o) setPricingLegId(null);
              }}
              leg={leg}
              onSaved={fetchLegs}
            />
          );
        })()}
    </>
  );
}
